import * as React from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { gammaPer1Pct as gammaPer1PctOf } from "@lib/marketCarry";

// ---------- formatting helpers ----------
export const dollars = (v: number | null | undefined, dp = 2) =>
  typeof v === "number" && isFinite(v)
    ? v.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      })
    : "—";

export const pct = (v: number | null | undefined, dp = 1) =>
  typeof v === "number" && isFinite(v) ? (v * 100).toFixed(dp) + "%" : "—";

/** Gamma read as the change in one contract's delta for a 1% index move
 *  (gamma x index level x 1%). Raw long-dated SPX gamma is ~0.0002 per index point,
 *  which reads as zero at any table precision. Null when either input is missing,
 *  never 0: a blank is "not known", a zero would claim the line has no curvature. */
export const gammaPer1Pct = (row: { gamma?: number | null; gammaUnderlyingPrice?: number | null }): number | null =>
  gammaPer1PctOf(row.gamma, row.gammaUnderlyingPrice);

export const GAMMA_PER_1PCT_TOOLTIP =
  "Gamma, shown as how much this contract's delta changes for a 1% move in the index " +
  "(gamma × index level × 1%). Calculated from IBKR's implied vol: IBKR rounds gamma to " +
  "3 decimals, which is zero on long-dated SPX.";

/** Force a clean YYYY-MM-DD regardless of input shape */
export const toYMD = (v: unknown): string => {
  if (v == null) return "—";
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const t = s.includes("T") ? s.slice(0, s.indexOf("T")) : s.split(" ")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;

  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);

  return s.slice(0, 10);
};

// ---------- types ----------
export type HeldLeapRow = {
  contractId: string;
  display: string;
  account: string;
  subAccount?: string;
  accountClass?: "MASTER" | "SUB" | "UNKNOWN";
  strike?: number;
  right?: "C" | "P" | string;
  exp?: string;
  dte?: number | null;
  qty?: number;
  entry?: number | null;
  market?: number | null;
  value?: number | null;
  costBasis?: number | null;
  unrealizedPnl?: number | null;
  returnPct?: number | null;
  peakPct?: number | null;
  ddFromPeak?: number | null;

  likelyPeaked?: boolean;
  exitHintReason?: string;
  exitHintScore?: number | null;
  exitHintPercentile?: number | null;
  // True absolute exit signal (rule+model blend, 0–1), read without the percentile
  // fallback that contaminates exitHintScore on some API shapes. Used to show the raw
  // score beside the within-book rank so "ranked high / low absolute signal" is visible.
  exitHintRawScore?: number | null;
  exitRiskRankOf?: number | null;

  ageDays?: number | null;
  drawdownAbs?: number | null;
  ponrScore?: number | null;
  likelyMature?: boolean;

  hardExit?: boolean;
  hardExitReason?: string | null;

  delta?: number | null;
  gamma?: number | null;
  /** Index level on the greeks snapshot the gamma was computed on (gamma_underlying_price). */
  gammaUnderlyingPrice?: number | null;
  theta?: number | null;
  vega?: number | null;
  impliedVol?: number | null;
  bid?: number | null;
  ask?: number | null;
  mid?: number | null;
  openInterest?: number | null;
  decisionPosture?: string | null;
  decisionDetail?: string | null;
  decisionPriority?: number | null;
};

export type FixedIncomeRow = {
  ticker: string;
  account?: string;
  weightPct?: number | null;
  value?: number | null;
  targetPct?: number | null;
  deltaPct?: number | null;
};

// ---------- internal helpers ----------
function toRankPercentile(row: HeldLeapRow): number | null {
  if (typeof row.exitHintPercentile === "number" && isFinite(row.exitHintPercentile)) {
    return Math.max(0, Math.min(100, row.exitHintPercentile));
  }
  if (typeof row.exitHintScore === "number" && isFinite(row.exitHintScore)) {
    return Math.max(0, Math.min(100, row.exitHintScore * 100));
  }
  return null;
}

function rankTag(p: number | null): string {
  if (p === null) return "—";
  const n = Math.max(1, Math.min(100, Math.round(p)));
  return `P${n}`;
}

function rankClass(p: number | null): string {
  // Monotonic and non-directional. Higher rank = more emphasis, and never up/down:
  // a within-book ORDER is not a gain or a loss, and green on a high exit rank read as
  // reassurance on the one cell where it is least warranted.
  if (p === null) return "bg-subtle/15 text-subtle border border-subtle/25";

  if (p >= 90) return "bg-elevated/20 text-elevated border border-elevated/30";
  if (p >= 75) return "bg-warn/15 text-warn border border-warn/25";
  if (p >= 60) return "bg-info/15 text-info border border-info/25";

  return "bg-subtle/15 text-subtle border border-subtle/25";
}

const Bar: React.FC<{
  value: number | null;
  max?: number;
  titlePrefix?: string;
}> = ({ value, max = 100, titlePrefix }) => {
  if (value == null || !isFinite(value)) {
    return <div className="h-2 w-28 rounded-full bg-subtle/20" />;
  }

  const width = Math.max(0, Math.min(100, (value / max) * 100));
  const title = titlePrefix ? `${titlePrefix} ${value.toFixed(1)}` : `${value.toFixed(1)}%`;

  return (
    <div className="h-2 w-28 overflow-hidden rounded-full bg-subtle/20" title={title}>
      <div className="h-2 rounded-full bg-info/70" style={{ width: `${width}%` }} />
    </div>
  );
};

const HardExitBadge: React.FC<{ active: boolean }> = ({ active }) => {
  return (
    <span
      className={[
        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold",
        active
          ? "border-down/30 bg-down/15 text-down"
          : "border-[var(--card-border)] bg-subtle/10 text-subtle",
      ].join(" ")}
      title={active ? "Hard exit condition is currently met" : "No hard exit condition"}
    >
      {active ? "Hard Exit" : "—"}
    </span>
  );
};

// ---------- Held LEAPS columns ----------
export const heldColumns: ColumnDef<HeldLeapRow>[] = [
  {
    id: "display",
    accessorKey: "display",
    header: "Display",
    cell: ({ row }) => <span className="font-medium text-[var(--text-primary)]">{row.original.display}</span>,
    size: 360,
    meta: { nowrap: false },
  },
  {
    id: "account",
    accessorKey: "account",
    header: "Account",
    cell: ({ row }) => row.original.account,
    size: 110,
    enableColumnFilter: true,
  },
  {
    id: "strike",
    accessorKey: "strike",
    header: "Strike",
    cell: ({ row }) => row.original.strike ?? "—",
    size: 80,
    meta: { numeric: true },
  },
  {
    id: "right",
    accessorKey: "right",
    header: "Right",
    cell: ({ row }) => row.original.right ?? "—",
    size: 60,
  },
  {
    id: "exp",
    accessorKey: "exp",
    header: "Exp.",
    cell: ({ row }) => toYMD(row.original.exp),
    size: 110,
  },
  {
    id: "dte",
    accessorKey: "dte",
    header: "DTE",
    cell: ({ row }) => row.original.dte ?? "—",
    size: 70,
    meta: { numeric: true },
  },
  {
    id: "qty",
    accessorKey: "qty",
    header: "Qty",
    cell: ({ row }) => row.original.qty ?? 0,
    size: 50,
    meta: { numeric: true },
  },
  {
    id: "entry",
    accessorKey: "entry",
    header: "Entry $",
    cell: ({ row }) => dollars(row.original.entry),
    size: 100,
    meta: { numeric: true },
  },
  {
    id: "market",
    accessorKey: "market",
    header: "Market $",
    cell: ({ row }) => dollars(row.original.market),
    size: 110,
    meta: { numeric: true },
  },
  {
    id: "value",
    accessorKey: "value",
    header: "Value",
    cell: ({ row }) => dollars(row.original.value),
    size: 120,
    meta: { numeric: true },
  },
  {
    id: "returnPct",
    accessorKey: "returnPct",
    header: "Return",
    cell: ({ row }) => pct(row.original.returnPct, 1),
    size: 90,
    enableSorting: true,
    meta: { numeric: true },
  },
  {
    id: "peakPct",
    accessorKey: "peakPct",
    header: "Peak%",
    cell: ({ row }) => pct(row.original.peakPct, 1),
    size: 90,
    meta: { numeric: true },
  },
  {
    id: "ddFromPeak",
    accessorKey: "ddFromPeak",
    header: "DD from Peak",
    cell: ({ row }) =>
      row.original.ddFromPeak == null ? "—" : pct(-Math.abs(row.original.ddFromPeak), 1),
    size: 120,
    meta: { numeric: true },
  },
  {
    id: "ageDays",
    accessorKey: "ageDays",
    header: "Age (d)",
    cell: ({ row }) => (typeof row.original.ageDays === "number" ? row.original.ageDays : "—"),
    size: 80,
    enableSorting: true,
    meta: { numeric: true },
  },
  {
    id: "drawdownAbs",
    accessorKey: "drawdownAbs",
    header: "DD Abs ($)",
    cell: ({ row }) => dollars(row.original.drawdownAbs),
    size: 110,
    enableSorting: true,
    meta: { numeric: true },
    sortingFn: (a, b) => {
      const av = a.original.drawdownAbs ?? -Infinity;
      const bv = b.original.drawdownAbs ?? -Infinity;
      return av === bv ? 0 : av > bv ? 1 : -1;
    },
  },
  {
    id: "ponrScore",
    accessorKey: "ponrScore",
    header: "PONR",
    cell: ({ row }) => {
      const raw = typeof row.original.ponrScore === "number" ? row.original.ponrScore : null;
      const v = raw == null ? null : Math.max(0, Math.min(100, raw));

      return (
        <div className="flex items-center gap-2">
          <Bar value={v} titlePrefix="PONR" />
          <span className="text-xs text-subtle">{v != null ? v.toFixed(0) : "—"}</span>
        </div>
      );
    },
    size: 150,
    enableSorting: true,
    sortingFn: (a, b) => {
      const av = a.original.ponrScore ?? -1;
      const bv = b.original.ponrScore ?? -1;
      return av === bv ? 0 : av > bv ? 1 : -1;
    },
  },
  {
    id: "likelyMature",
    accessorKey: "likelyMature",
    header: "Likely Mature",
    cell: ({ row }) => (
      <span
        className={[
          "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
          row.original.likelyMature
            ? "border-warn/30 bg-warn/15 text-warn"
            : "border-[var(--card-border)] bg-subtle/10 text-subtle",
        ].join(" ")}
      >
        {row.original.likelyMature ? "Yes" : "No"}
      </span>
    ),
    size: 110,
    enableColumnFilter: true,
    filterFn: (row, id, value) => {
      const v = row.getValue<boolean>(id);
      return value ? Boolean(v) : true;
    },
  },
  {
    id: "hardExit",
    accessorKey: "hardExit",
    header: "Hard Exit",
    cell: ({ row }) => <HardExitBadge active={!!row.original.hardExit} />,
    size: 110,
    enableSorting: true,
    sortingFn: (a, b) => {
      const av = a.original.hardExit ? 1 : 0;
      const bv = b.original.hardExit ? 1 : 0;
      return av === bv ? 0 : av > bv ? 1 : -1;
    },
    enableColumnFilter: true,
    filterFn: (row, id, value) => {
      const v = row.getValue<boolean>(id);
      return value ? Boolean(v) : true;
    },
  },
  {
    id: "hardExitReason",
    accessorKey: "hardExitReason",
    header: "Hard Exit Reason",
    cell: ({ row }) => row.original.hardExitReason || "—",
    size: 220,
    meta: { nowrap: false },
  },
  {
    id: "likelyPeaked",
    accessorKey: "likelyPeaked",
    header: "Likely Peaked",
    cell: ({ row }) => (
      <span
        className={[
          "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
          row.original.likelyPeaked
            ? "border-elevated/30 bg-elevated/15 text-elevated"
            : "border-[var(--card-border)] bg-subtle/10 text-subtle",
        ].join(" ")}
      >
        {row.original.likelyPeaked ? "Yes" : "No"}
      </span>
    ),
    size: 110,
    enableColumnFilter: true,
    filterFn: (row, id, value) => {
      const v = row.getValue<boolean>(id);
      return value ? Boolean(v) : true;
    },
  },
  {
    id: "exitHintScore",
    accessorKey: "exitHintScore",
    header: "Exit Risk",
    cell: ({ row }) => {
      const score01 =
        (typeof row.original.exitHintScore === "number" && isFinite(row.original.exitHintScore)
          ? row.original.exitHintScore
          : null) ??
        (typeof row.original.exitHintPercentile === "number" && isFinite(row.original.exitHintPercentile)
          ? row.original.exitHintPercentile / 100
          : null);

      const pctVal = score01 != null ? score01 * 100 : null;

      return (
        <div className="flex items-center gap-2">
          <Bar value={pctVal} />
          <span className="text-xs text-subtle">
            {pctVal != null ? `${pctVal.toFixed(1)}%` : "—"}
          </span>
        </div>
      );
    },
    size: 160,
    enableSorting: true,
    sortingFn: (a, b) => {
      const aScore =
        (typeof a.original.exitHintScore === "number" ? a.original.exitHintScore : null) ??
        (typeof a.original.exitHintPercentile === "number"
          ? a.original.exitHintPercentile / 100
          : null) ??
        -1;

      const bScore =
        (typeof b.original.exitHintScore === "number" ? b.original.exitHintScore : null) ??
        (typeof b.original.exitHintPercentile === "number"
          ? b.original.exitHintPercentile / 100
          : null) ??
        -1;

      return aScore === bScore ? 0 : aScore > bScore ? 1 : -1;
    },
  },
  {
    id: "rank",
    header: "Rank",
    cell: ({ row }) => {
      const p = toRankPercentile(row.original);
      const tag = rankTag(p);
      const cls = rankClass(p);

      return (
        <span
          className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${cls}`}
        >
          {tag}
        </span>
      );
    },
    size: 80,
    enableSorting: true,
    sortingFn: (a, b) => {
      const av = toRankPercentile(a.original) ?? -1;
      const bv = toRankPercentile(b.original) ?? -1;
      return av === bv ? 0 : av > bv ? 1 : -1;
    },
  },
  {
    id: "exitHint",
    accessorKey: "exitHintReason",
    header: "Signal",
    cell: ({ row }) => row.original.exitHintReason ?? "—",
    size: 180,
    meta: { nowrap: false },
  },
  {
    id: "decisionPosture",
    accessorKey: "decisionPosture",
    header: "Posture",
    cell: ({ row }) => row.original.decisionPosture ?? "—",
    size: 120,
  },
  {
    id: "decisionDetail",
    accessorKey: "decisionDetail",
    header: "Why Now",
    cell: ({ row }) => row.original.decisionDetail ?? "—",
    size: 360,
    meta: { nowrap: false },
  },
  {
    id: "delta",
    accessorKey: "delta",
    header: "Delta",
    cell: ({ row }) =>
      typeof row.original.delta === "number" && isFinite(row.original.delta)
        ? row.original.delta.toFixed(2)
        : "—",
    size: 90,
    meta: { numeric: true },
  },
  {
    id: "gamma",
    accessorFn: (r) => gammaPer1Pct(r),
    header: "Gamma (Δ/1%)",
    cell: ({ row }) => {
      const v = gammaPer1Pct(row.original);
      if (v === null) {
        const why =
          typeof row.original.gamma === "number"
            ? "No index level on this line's greeks snapshot, so gamma cannot be scaled to a 1% move."
            : "No gamma on this line's greeks snapshot.";
        return <span title={why}>—</span>;
      }
      const raw = row.original.gamma as number;
      return <span title={`${GAMMA_PER_1PCT_TOOLTIP} Raw gamma ${raw.toPrecision(3)}.`}>{v.toFixed(3)}</span>;
    },
    size: 110,
    meta: { numeric: true },
  },
  {
    id: "vega",
    accessorKey: "vega",
    header: "Vega",
    cell: ({ row }) =>
      typeof row.original.vega === "number" && isFinite(row.original.vega)
        ? row.original.vega.toFixed(2)
        : "—",
    size: 90,
    meta: { numeric: true },
  },
  {
    id: "impliedVol",
    accessorKey: "impliedVol",
    header: "IV",
    cell: ({ row }) => pct(row.original.impliedVol, 1),
    size: 80,
    meta: { numeric: true },
  },
  /* ---- Pre-trade liquidity -------------------------------------------------
   * These were only ever visible inside the per-line detail strips, which is why
   * the plan recorded them as unique to Portfolio: Signals' grid carries LastPrice
   * only. Since every exit is executed by hand in TWS, "how wide is the market on
   * the thing I am about to sell" is a real pre-trade question, and it belongs in a
   * sortable column rather than buried in a card. */
  {
    id: "bid",
    accessorKey: "bid",
    header: "Bid",
    cell: ({ row }) => dollars(row.original.bid),
    size: 90,
    meta: { numeric: true },
  },
  {
    id: "ask",
    accessorKey: "ask",
    header: "Ask",
    cell: ({ row }) => dollars(row.original.ask),
    size: 90,
    meta: { numeric: true },
  },
  {
    id: "spreadPct",
    header: "Spread %",
    /* Sorts on the computed value, not on a formatted string. Withheld entirely
     * unless BOTH sides are present and the mid is positive -- a one-sided quote
     * cannot produce a spread, and inventing one from the stored mark is exactly
     * the substitution this page refuses to make elsewhere. */
    accessorFn: (r: HeldLeapRow) => {
      const b = typeof r.bid === "number" && isFinite(r.bid) ? r.bid : null;
      const a = typeof r.ask === "number" && isFinite(r.ask) ? r.ask : null;
      // ask < bid is a CROSSED quote -- a feed problem, not a negative spread.
      // Withhold rather than render a nonsense number (the helper this replaced
      // carried the same guard).
      if (b === null || a === null || a < b) return null;
      const mid = (b + a) / 2;
      return mid > 0 ? (a - b) / mid : null;
    },
    cell: ({ row }) => {
      const b = row.original.bid;
      const a = row.original.ask;
      if (typeof b !== "number" || typeof a !== "number" || !isFinite(b) || !isFinite(a)) return "—";
      if (a < b) return "—"; // crossed quote
      const mid = (b + a) / 2;
      if (!(mid > 0)) return "—";
      return pct((a - b) / mid, 1);
    },
    size: 100,
    meta: { numeric: true },
  },
  {
    id: "openInterest",
    accessorKey: "openInterest",
    header: "OI",
    /* Often null: open_interest COALESCEs through dbo.OptionPrices, dead since
     * 2025, and the frontend hydration that backfills it swallows its own errors.
     * A blank here is "not known", never "zero interest". */
    cell: ({ row }) =>
      typeof row.original.openInterest === "number" && isFinite(row.original.openInterest)
        ? row.original.openInterest.toLocaleString()
        : "—",
    size: 90,
    meta: { numeric: true },
  },
];

// ---------- Fixed income table ----------
export const fixedIncomeColumns: ColumnDef<FixedIncomeRow>[] = [
  {
    id: "ticker",
    accessorKey: "ticker",
    header: "Ticker",
    cell: ({ row }) => <span className="font-medium text-[var(--text-primary)]">{row.original.ticker}</span>,
    size: 140,
  },
  {
    id: "account",
    accessorKey: "account",
    header: "Account",
    cell: ({ row }) => row.original.account ?? "—",
    size: 120,
  },
  {
    id: "weightPct",
    accessorKey: "weightPct",
    header: "Weight",
    cell: ({ row }) => pct(row.original.weightPct, 2),
    size: 90,
    meta: { numeric: true },
  },
  {
    id: "value",
    accessorKey: "value",
    header: "Value",
    cell: ({ row }) => dollars(row.original.value),
    size: 120,
    meta: { numeric: true },
  },
];